import React, { useState, useEffect } from 'react';
import { X, Send, Calculator, Euro, Loader2, Check, Info } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const COMMISSION_RATE = 0.10; // 10% total commission
const CUSTOMER_FEE_RATE = 0.05; // 5% customer pays
const TRANSPORTER_FEE_RATE = 0.05; // 5% transporter pays

const QuoteFormModal = ({ isOpen, onClose, tripRequest, transporterUserId, onQuoteSubmitted }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    transportPrice: '',
    estimatedDuration: '',
    serviceType: 'standard',
    notes: ''
  });

  // Calculated values
  const [calculations, setCalculations] = useState({
    transportPrice: 0,
    commissionTotal: 0,
    customerFee: 0,
    transporterFee: 0,
    customerTotal: 0,
    transporterEarnings: 0
  });

  // Recalculate whenever transport price changes
  useEffect(() => {
    const transport = parseFloat(formData.transportPrice) || 0;
    
    const commissionTotal = transport * COMMISSION_RATE;
    const customerFee = transport * CUSTOMER_FEE_RATE;
    const transporterFee = transport * TRANSPORTER_FEE_RATE;
    const customerTotal = transport + customerFee;
    const transporterEarnings = transport - transporterFee;

    setCalculations({
      transportPrice: Math.round(transport * 100) / 100,
      commissionTotal: Math.round(commissionTotal * 100) / 100,
      customerFee: Math.round(customerFee * 100) / 100,
      transporterFee: Math.round(transporterFee * 100) / 100,
      customerTotal: Math.round(customerTotal * 100) / 100,
      transporterEarnings: Math.round(transporterEarnings * 100) / 100
    });
  }, [formData.transportPrice, COMMISSION_RATE, CUSTOMER_FEE_RATE, TRANSPORTER_FEE_RATE]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.transportPrice || parseFloat(formData.transportPrice) <= 0) {
      setError('Please enter a valid price');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      const response = await fetch(`${API}/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          trip_request_id: tripRequest.request_id,
          transporter_user_id: transporterUserId,
          transport_price: parseFloat(formData.transportPrice),
          estimated_duration: formData.estimatedDuration,
          service_type: formData.serviceType,
          notes: formData.notes
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to submit quote');
      }

      const quoteData = await response.json();
      setIsSubmitted(true);
      
      if (onQuoteSubmitted) {
        onQuoteSubmitted(quoteData);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      transportPrice: '',
      estimatedDuration: '',
      serviceType: 'standard',
      notes: ''
    });
    setIsSubmitted(false);
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-[#0f0f0f] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4 shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-[#0f0f0f] border-b border-white/10 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white">Send Quote</h2>
            <p className="text-gray-400 text-sm mt-1">
              Trip: {tripRequest?.request_id} • {tripRequest?.pickup_address} → {tripRequest?.dropoff_address}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="w-10 h-10 bg-white/5 hover:bg-white/10 rounded-full flex items-center justify-center text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {isSubmitted ? (
            /* Success State */
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                <Check className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Quote Submitted!</h3>
              <p className="text-gray-400 mb-6">
                Customer will pay <span className="text-amber-500 font-bold">MAD {calculations.customerTotal.toFixed(2)}</span> (Gross)
              </p>
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
                <p className="text-gray-300">
                  Your earnings: <span className="text-green-400 font-bold">MAD {calculations.transporterEarnings.toFixed(2)}</span> (Gross, after 5% fee)
                </p>
              </div>
              <Button
                onClick={handleClose}
                className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-8 py-3 rounded-full"
              >
                Done
              </Button>
            </div>
          ) : (
            /* Form */
            <form onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6">
                  <p className="text-red-500 text-sm">{error}</p>
                </div>
              )}

              {/* Trip Summary */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
                <h4 className="text-white font-semibold mb-3">Trip Details</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-400">Horses</p>
                    <p className="text-white">{tripRequest?.horse_count}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Date</p>
                    <p className="text-white">{tripRequest?.transport_date}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Customer</p>
                    <p className="text-white">{tripRequest?.first_name} {tripRequest?.last_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Country</p>
                    <p className="text-white">{tripRequest?.country}</p>
                  </div>
                </div>
              </div>

              {/* Price Input */}
              <div className="mb-6">
                <Label className="text-gray-300 mb-2 block">
                  <Euro className="w-4 h-4 inline mr-2" />
                  Transport Price (Gross) <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg">MAD </span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.transportPrice}
                    onChange={(e) => setFormData(prev => ({ ...prev, transportPrice: e.target.value }))}
                    placeholder="0.00"
                    className="bg-black/30 border-white/10 text-white text-2xl font-bold h-14 pl-10 rounded-xl focus:border-amber-500"
                  />
                </div>
                <p className="text-gray-500 text-xs mt-1">Enter your gross transport price</p>
              </div>

              {/* Service Type */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <Label className="text-gray-300 mb-2 block">Service Type</Label>
                  <Select 
                    value={formData.serviceType}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, serviceType: value }))}
                  >
                    <SelectTrigger className="bg-black/30 border-white/10 text-white h-12 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1a1a1a] border-white/10">
                      <SelectItem value="standard" className="text-white">Standard</SelectItem>
                      <SelectItem value="premium" className="text-white">Premium</SelectItem>
                      <SelectItem value="express" className="text-white">Express</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300 mb-2 block">Estimated Duration</Label>
                  <Input
                    value={formData.estimatedDuration}
                    onChange={(e) => setFormData(prev => ({ ...prev, estimatedDuration: e.target.value }))}
                    placeholder="e.g., 6-8 hours"
                    className="bg-black/30 border-white/10 text-white h-12 rounded-xl"
                  />
                </div>
              </div>

              {/* Notes */}
              <div className="mb-6">
                <Label className="text-gray-300 mb-2 block">Additional Notes (Optional)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any special conditions, included services, etc."
                  rows={3}
                  className="bg-black/30 border-white/10 text-white rounded-xl resize-none"
                />
              </div>

              {/* Price Breakdown */}
              {calculations.transportPrice > 0 && (
                <div className="bg-gradient-to-br from-amber-900/20 to-amber-800/10 border border-amber-500/30 rounded-xl p-6 mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Calculator className="w-5 h-5 text-amber-500" />
                    <h4 className="text-white font-semibold">Price Breakdown (Gross)</h4>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Transport Price</span>
                      <span className="text-white font-mono">MAD {calculations.transportPrice.toFixed(2)}</span>
                    </div>
                    
                    <div className="pt-3 border-t border-white/10">
                      <p className="text-gray-400 text-sm mb-2 flex items-center gap-1">
                        <Info className="w-3 h-3" /> Commission Split (10% total)
                      </p>
                      <div className="flex justify-between text-blue-400">
                        <span>Customer Fee (+5%)</span>
                        <span className="font-mono">+MAD {calculations.customerFee.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-red-400 mt-1">
                        <span>Your Fee (-5%)</span>
                        <span className="font-mono">-MAD {calculations.transporterFee.toFixed(2)}</span>
                      </div>
                    </div>
                    
                    <div className="pt-3 border-t border-white/10">
                      <div className="flex justify-between">
                        <span className="text-gray-300">Customer Pays (Gross)</span>
                        <span className="text-amber-500 font-mono font-bold">MAD {calculations.customerTotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-green-400 font-semibold mt-2">
                        <span>You Receive (Gross)</span>
                        <span className="font-mono text-lg">MAD {calculations.transporterEarnings.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting || !formData.transportPrice}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-4 rounded-full text-lg disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting Quote...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Submit Quote (Customer pays MAD {calculations.customerTotal.toFixed(2)})
                  </>
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteFormModal;
